import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

/**
 * Konfiguracja post-processing (bloom effect) dla WebGL
 */
export function setupWebGLPostProcessing(renderer, scene, camera, bloomConfig, renderConfig) {
    const resolutionScale = renderConfig.resolutionScale || 1.0;
    const devicePixelRatio = window.devicePixelRatio || 1.0;
    const targetPixelRatio = renderConfig.pixelRatio !== null && renderConfig.pixelRatio !== undefined
        ? renderConfig.pixelRatio 
        : Math.min(devicePixelRatio, 2.0);
    
    const targetWidth = Math.floor(window.innerWidth * resolutionScale);
    const targetHeight = Math.floor(window.innerHeight * resolutionScale);
    
    const composer = new EffectComposer(renderer);
    
    // RenderPass - renderuje scenę do tekstury
    const renderPass = new RenderPass(scene, camera);
    renderPass.clear = true;
    renderPass.clearColor = new THREE.Color(0x000000);
    renderPass.clearAlpha = 0;
    renderPass.renderToScreen = false;
    composer.addPass(renderPass);
    
    // UnrealBloomPass - bloom effect
    const bloomPass = new UnrealBloomPass(
        new THREE.Vector2(targetWidth, targetHeight),
        bloomConfig.strength || 1.5,
        bloomConfig.radius || 0.4,
        bloomConfig.threshold || 0.8
    );
    bloomPass.clear = false;
    bloomPass.renderToScreen = false;
    composer.addPass(bloomPass);
    
    // FXAA - Fast Approximate Anti-Aliasing
    const fxaaPass = new ShaderPass(FXAAShader);
    fxaaPass.material.uniforms['resolution'].value.x = 1 / (targetWidth * targetPixelRatio);
    fxaaPass.material.uniforms['resolution'].value.y = 1 / (targetHeight * targetPixelRatio);
    fxaaPass.renderToScreen = true;
    composer.addPass(fxaaPass);
    
    console.log('[PostProcessing] ✅ WebGL bloom (EffectComposer) skonfigurowany');
    
    return composer;
}

/**
 * Konfiguracja post-processing (bloom effect) dla WebGPU
 */
export async function setupWebGPUPostProcessing(renderer, scene, camera, bloomConfig) {
    try {
        const tslModule = await import('three/tsl');
        const bloomModule = await import('three/addons/tsl/display/BloomNode.js');
        const webGPUModule = await import('three/webgpu');
        const { PostProcessing } = webGPUModule;
        
        if (!tslModule.pass || !bloomModule.bloom || !PostProcessing) {
            console.warn('[PostProcessing] TSL lub PostProcessing nie jest dostępny, wyłączam bloom');
            return null;
        }
        
        const postProcessing = new PostProcessing(renderer);
        const scenePass = tslModule.pass(scene, camera);
        const scenePassColor = scenePass.getTextureNode('output');
        
        if (bloomConfig.enabled) {
            const bloomPass = bloomModule.bloom(scenePassColor);
            bloomPass.threshold.value = bloomConfig.threshold || 0.1;
            bloomPass.strength.value = bloomConfig.strength || 0.5;
            bloomPass.radius.value = bloomConfig.radius || 0.4;
            postProcessing.outputNode = scenePassColor.add(bloomPass);
        } else {
            postProcessing.outputNode = scenePassColor;
        }
        
        console.log('[PostProcessing] ✅ WebGPU bloom (TSL) skonfigurowany');
        
        return {
            postProcessing,
            scenePass,
            scenePassColor,
            bloomModule
        };
    } catch (e) {
        console.warn('[PostProcessing] Błąd konfiguracji WebGPU bloom:', e);
        return null;
    }
}

/**
 * Aktualizacja rozdzielczości FXAA w EffectComposer
 */
export function updateFXAAResolution(composer, targetWidth, targetHeight, targetPixelRatio) {
    const passes = composer.passes;
    for (let i = 0; i < passes.length; i++) {
        const pass = passes[i];
        if (pass instanceof ShaderPass && pass.material.uniforms['resolution']) {
            pass.material.uniforms['resolution'].value.x = 1 / (targetWidth * targetPixelRatio);
            pass.material.uniforms['resolution'].value.y = 1 / (targetHeight * targetPixelRatio);
        }
    }
}

